import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SearchIcon, Typography } from '@/components/ui';
import { useClickOutside, useDebounce } from '@/hooks';
import { customerKeys, customerService } from '@/services/customers';
import { type SearchResult, useSearch } from '@/services/search';
import { cn } from '@/lib/utils';
import { getMatchDisplayText, getMatchLabel } from './matchLabel';

interface SearchBarProps {
  onResultSelect?: (result: SearchResult) => void;
  /**
   * Called with the raw text when Enter is pressed without a highlighted
   * suggestion, and with '' when the user clears the input. Drives the
   * client-master list filter (SCRUM-1109).
   */
  onSubmit?: (query: string) => void;
  /**
   * The externally-held submitted query. The input initializes from it and
   * re-syncs whenever it changes (e.g. cleared elsewhere); in-progress typing
   * is otherwise local until submitted.
   */
  submittedQuery?: string;
  placeholder?: string;
  className?: string;
  minSearchLength?: number;
}

export function SearchBar({
  onResultSelect,
  onSubmit,
  submittedQuery = '',
  placeholder = 'Search by name, client number, email, mobile, or DOB...',
  className = '',
  minSearchLength = 2,
}: SearchBarProps) {
  const [searchTerm, setSearchTerm] = useState(submittedQuery);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isFetching, error } = useSearch(
    debouncedSearchTerm,
    {
      limit: 8,
      minSearchLength,
    }
  );

  const results = data?.results || [];
  const shouldShowDropdown = isOpen && searchTerm.length >= minSearchLength;

  const closeDropdown = useCallback(() => setIsOpen(false), []);
  useClickOutside(containerRef, closeDropdown, isOpen);

  // Prefetch customer detail on hover (300ms debounce)
  const queryClient = useQueryClient();
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleResultHover = useCallback(
    (userId: string) => {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = setTimeout(() => {
        queryClient.prefetchQuery({
          queryKey: customerKeys.detail(userId),
          queryFn: () => customerService.getCustomer(userId),
          staleTime: 5 * 60 * 1000,
        });
      }, 300);
    },
    [queryClient]
  );

  const handleResultHoverEnd = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
  }, []);

  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1);
  }, [results.length, debouncedSearchTerm]);

  // Re-sync the input when the submitted query changes outside this component
  // (e.g. the ClientsPage "Clear search" affordance).
  useEffect(() => {
    setSearchTerm(submittedQuery);
  }, [submittedQuery]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex < 0 || !listboxRef.current) return;
    const activeElement = listboxRef.current.querySelector(
      `[data-index="${activeIndex}"]`
    );
    activeElement?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= minSearchLength) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    // Removing the search text resets the filtered list without needing Enter.
    if (value === '' && submittedQuery !== '') {
      onSubmit?.('');
    }
  };

  const handleInputFocus = () => {
    if (searchTerm.length >= minSearchLength) {
      setIsOpen(true);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    onResultSelect?.(result);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // A highlighted suggestion wins; plain Enter submits the raw text as
      // the client-master list filter.
      if (
        shouldShowDropdown &&
        activeIndex >= 0 &&
        activeIndex < results.length
      ) {
        const result = results[activeIndex];
        if (result) handleResultClick(result);
        return;
      }
      if (onSubmit) {
        onSubmit(searchTerm.trim());
        setIsOpen(false);
      }
      return;
    }

    if (!shouldShowDropdown || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  const renderDropdownContent = () => {
    if (isLoading && !data) {
      return (
        <div className="px-4 py-3">
          <Typography
            variant="body-regular"
            as="p"
            className="text-neutral-500"
          >
            Searching...
          </Typography>
        </div>
      );
    }

    if (error && !data) {
      return (
        <div className="px-4 py-3">
          <Typography variant="body-regular" as="p" className="text-error-500">
            Search failed. Please try again.
          </Typography>
        </div>
      );
    }

    if (results.length === 0 && isFetching) {
      return (
        <div className="px-4 py-3">
          <Typography
            variant="body-regular"
            as="p"
            className="text-neutral-500"
          >
            Searching...
          </Typography>
        </div>
      );
    }

    if (results.length === 0 && !isFetching) {
      return (
        <div className="px-4 py-3">
          <Typography
            variant="body-regular"
            as="p"
            className="text-neutral-500"
          >
            No matches found.
          </Typography>
        </div>
      );
    }

    return (
      <div
        className="py-1"
        ref={listboxRef}
        role="listbox"
        id="search-results-listbox"
      >
        {isFetching && (
          <div className="px-4 py-1.5 border-b border-neutral-100">
            <Typography
              variant="footnote-description"
              as="p"
              className="text-neutral-400"
            >
              Updating results...
            </Typography>
          </div>
        )}
        {results.map((result, index) => (
          <button
            key={`${result.entityType}-${result.userId}`}
            data-index={index}
            role="option"
            id={`search-result-${index}`}
            aria-selected={index === activeIndex}
            onClick={() => handleResultClick(result)}
            onMouseEnter={() => handleResultHover(result.userId)}
            onMouseLeave={handleResultHoverEnd}
            className={cn(
              'w-full text-left px-4 py-3 transition-colors border-b border-neutral-100 last:border-b-0',
              index === activeIndex ? 'bg-neutral-100' : 'hover:bg-neutral-50'
            )}
          >
            <div>
              <Typography
                variant="body-medium"
                as="p"
                className="text-neutral-900 truncate"
              >
                {result.firstName && result.lastName
                  ? `${result.firstName} ${result.lastName}`
                  : result.matchedText}
              </Typography>
              <Typography
                variant="footnote-description"
                as="p"
                className="text-neutral-500 truncate mt-0.5"
              >
                <span className="text-neutral-400">
                  {getMatchLabel(result, debouncedSearchTerm)}:
                </span>{' '}
                {getMatchDisplayText(result, debouncedSearchTerm)}
              </Typography>
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div className="relative">
        <SearchIcon
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-n200"
        />
        {isFetching && !isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="h-4 w-4 border-2 border-neutral-300 border-t-primary-6 rounded-full animate-spin" />
          </div>
        )}
        <input
          type="search"
          aria-label="Search clients"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={shouldShowDropdown}
          aria-haspopup="listbox"
          aria-controls="search-results-listbox"
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `search-result-${activeIndex}` : undefined
          }
          className="w-full pl-10 pr-10 py-2 bg-white border border-neutral-100 rounded-lg font-roboto text-[14px] leading-5 font-normal text-neutral-n700 placeholder:text-neutral-n200 focus:outline-none focus:ring-1 focus:ring-primary-6"
        />
      </div>

      {shouldShowDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-neutral-200 max-h-[400px] overflow-y-auto z-50">
          {renderDropdownContent()}
        </div>
      )}
    </div>
  );
}
